import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle, UploadSimple, Warning } from "@phosphor-icons/react";
import { BACKEND_URL } from "../lib/api";

const API = `${BACKEND_URL}/api/doc-requests/public`;
const inputCls = "w-full rounded-md border border-[#E2E0D8] bg-white px-3 py-2 text-sm focus:outline-none focus:border-brandblue";

function BankingForm({ token, onDone }) {
  const [f, setF] = useState({ bank_name: "", bank_routing: "", bank_account_last4: "", bank_account_type: "checking" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const save = async () => {
    setBusy(true);
    try { const { data } = await axios.post(`${API}/${token}/banking`, f); onDone(data); toast.success("Banking saved"); }
    catch (e) { toast.error(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Check the routing (9 digits) and account (last 4 digits)."); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-3 grid grid-cols-2 gap-2" data-testid="upload-banking-form">
      <input className={inputCls} placeholder="Bank name" value={f.bank_name} onChange={(e) => set("bank_name", e.target.value)} data-testid="upload-bank-name" />
      <select className={inputCls} value={f.bank_account_type} onChange={(e) => set("bank_account_type", e.target.value)} data-testid="upload-bank-type">
        <option value="checking">Checking</option><option value="savings">Savings</option>
      </select>
      <input className={inputCls} placeholder="Routing # (9 digits)" maxLength={9} value={f.bank_routing} onChange={(e) => set("bank_routing", e.target.value.replace(/\D/g, ""))} data-testid="upload-bank-routing" />
      <input className={inputCls} placeholder="Account # (last 4)" maxLength={4} value={f.bank_account_last4} onChange={(e) => set("bank_account_last4", e.target.value.replace(/\D/g, ""))} data-testid="upload-bank-last4" />
      <button onClick={save} disabled={busy} className="col-span-2 rounded-md bg-forest text-white py-2 text-sm font-bold disabled:opacity-60" data-testid="upload-bank-save">{busy ? "Saving…" : "Save banking"}</button>
    </div>
  );
}

function Item({ item, token, onDone }) {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error("Max 15 MB"); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await axios.post(`${API}/${token}/upload/${item.key}`, fd);
      onDone(data); toast.success(`${item.label} uploaded`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-[#E2E0D8] bg-white p-4" data-testid={`upload-item-${item.key}`}>
      <div className="flex items-center justify-between">
        <div className="font-semibold text-sm text-ink">{item.label}</div>
        {item.done
          ? <span className="inline-flex items-center gap-1 text-xs font-bold text-forest"><CheckCircle size={16} weight="fill" /> Received</span>
          : item.type === "file" && (
            <label className="inline-flex items-center gap-1.5 cursor-pointer rounded-md bg-brandblue text-white px-3 py-1.5 text-xs font-bold">
              <UploadSimple size={14} /> {busy ? "Uploading…" : "Upload"}
              <input type="file" accept=".pdf,.png,.jpg,.jpeg" className="hidden" disabled={busy}
                onChange={(e) => upload(e.target.files?.[0])} data-testid={`upload-input-${item.key}`} />
            </label>
          )}
      </div>
      {!item.done && item.type === "banking" && <BankingForm token={token} onDone={onDone} />}
    </div>
  );
}

export default function DocUploadPage() {
  const { token } = useParams();
  const [req, setReq] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/${token}`).then(({ data }) => setReq(data))
      .catch((e) => setError(e?.response?.data?.detail || "This link is not valid."));
  }, [token]);

  return (
    <main className="min-h-screen bg-bone px-4 py-10" data-testid="doc-upload-page">
      <div className="max-w-lg mx-auto">
        <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-brandblue">MedexOVA onboarding</div>
        <h1 className="mt-1 font-heading text-3xl font-black text-ink">Upload your documents</h1>
        {error && <div className="mt-6 rounded-xl border border-rose-300 bg-rose-50 p-4 text-sm flex gap-2" data-testid="doc-upload-error"><Warning size={18} className="text-rose-500" />{error}</div>}
        {req && (
          <>
            <p className="mt-2 text-sm text-slate-500">{req.applicant_name ? `For ${req.applicant_name}. ` : ""}PDF, JPG or PNG, up to 15 MB each.</p>
            {req.status === "completed" ? (
              <div className="mt-6 rounded-xl border border-forest/30 bg-forest/5 p-5 text-sm font-semibold text-forest flex gap-2" data-testid="doc-upload-complete">
                <CheckCircle size={20} weight="fill" /> All set — we received everything. Our team will take it from here.
              </div>
            ) : (
              <div className="mt-6 space-y-3">{req.items.map((i) => <Item key={i.key} item={i} token={token} onDone={setReq} />)}</div>
            )}
          </>
        )}
        <p className="mt-6 text-center text-[11px] text-slate-500">Need help? Call (833) 659-2699.</p>
      </div>
    </main>
  );
}
